/**
 * Created By Mantu Kumar 
 * Mail: developer.mantu@gmail.com
 * URL & Query String Utilities
 */

/**
 * Build query string from object
 * @param {Object} params
 * @returns {string}
 */
export const buildQueryString = (params = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (Array.isArray(value)) {
            if (value.length) {
                searchParams.set(key, value.join(","));
            }
        } else {
            searchParams.set(key, value);
        }
    });
    return searchParams.toString();
};

/**
 * Parse query string into object
 * @param {string} search
 * @returns {Object}
 */
export const parseQueryString = (search = "") => {
    const params = new URLSearchParams(search);
    const result = {};
    for (const [key, value] of params.entries()) {
        const decoded = decodeURIComponent(value.replace(/\+/g, " "));

        result[key] = decoded.includes(",")
            ? decoded.split(",").map(v => v.trim())
            : decoded;
    }
    return result;
};

/**
 * Update URL query params (merge)
 * @param {Object} currentParams
 * @param {Object} updates
 * @returns {string}
 */
export const updateQueryParams = (currentParams = {}, updates = {}) => {
    return buildQueryString({
        ...currentParams,
        ...updates,
    });
};

/**
 * Remove specific query params
 * @param {Object} params
 * @param {string[]} keys
 * @returns {string}
 */
export const removeQueryParams = (params = {}, keys = []) => {
    const updated = { ...params };
    keys.forEach((key) => delete updated[key]);
    return buildQueryString(updated);
};

/**
 * Create product PDP URL
 * product-slug/p/sku
 *
 * @param {string} slug
 * @param {string|number} sku
 * @returns {string}
 */
export const getProductUrl = (slug, sku) => {
    if (!slug || !sku) return "#";
    const cleanSlug = normalizeSlug(slug);
    const cleanSku = String(sku).trim();
    return `/products/${cleanSlug}/p/${cleanSku}`;
};

/**
 * Create category PLP URL
 * @param {string} category
 * @param {string} subcategory
 * @returns {string}
 */
export const getCategoryUrl = (category, subcategory) => {
    if (!category) return "/";
    return subcategory
        ? `/${category}/${subcategory}`
        : `/${category}`;
};

/**
 * Create category PLP URL
 * @param {string} category
 * @param {string} subcategory
 * @returns {string}
 */
export const getListingUrl = (text) => {
    const baseUrl = process.env.REACT_APP_BASE_URL || "";
    if (!text) return baseUrl || "/";
    return `${baseUrl}${text}`;
};

/**
 * Clean slug:
 * - removes leading/trailing slashes
 * - replaces multiple slashes with single slash
 */
const normalizeSlug = (slug = "") => {
    return String(slug)
        .trim()
        .replace(/\/+/g, "/")     
        .replace(/^\/|\/$/g, "");
};

/**
 * Build Product Detail Page (PDP) URL
 * Pattern: /products/:slug/p/:sku
 *
 * @param {string} slug
 * @param {string|number} sku
 * @returns {string}
 */
// export const getProductPdpUrl = (slug = "", sku = "") => {
//     if (!slug || !sku) return slug;

//     const safeSlug = encodeURIComponent(slug);
//     const safeSku = encodeURIComponent(sku);
//     return `/${safeSlug}/${safeSku}/buy`;
// };

export const getProductPdpUrl = (slug = "", sku = "") => {
    if (!slug || !sku) return "";
    const cleanSlug = normalizeSlug(slug);
    const cleanSku = String(sku).trim();
    return `/${cleanSlug}/${cleanSku}/buy`;
};

/**
 * Build Product Detail Page (PDP) URL
 * Pattern: /products/:slug/p/:sku
 *
 * @param {string} slug
 * @param {string|number} sku
 * @returns {string}
 */
// export const getProductPdpUrlVariant = (slug = "", sku = "", variant = "") => {
//     if (!slug || !sku) return slug;
//     const safeSlug = encodeURIComponent(slug);
//     const safeSku = encodeURIComponent(sku);
//     return `/${safeSlug}/${safeSku}/buy?variant=${variant}`;
// };

export const getProductPdpUrlVariant = (slug = "", sku = "", variant = "") => {
    if (!slug || !sku) return "";
    const cleanSlug = normalizeSlug(slug);
    const cleanSku = String(sku).trim();
    const cleanVariant = encodeURIComponent(variant || "");
    return `/${cleanSlug}/${cleanSku}/buy?variant=${cleanVariant}`;
};
/**
 * Build Slugify URL
 * Pattern: /products/:slug/p/:sku
 *
 * @param {string} slug
 * @param {string|number} sku
 * @returns {string}
 */
export const slugToLabel = (slug = "") =>
    slug
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Build query string from object or array of objects
 * @param {Object|Array} params
 * @param {Object} keyMap - Optional key mapping
 * @returns {string}
 */
export const customBuildQueryString = (params = {}, keyMap = {}) => {
    if (!params) return "";

    // Normalize array of objects into single object
    const normalized = Array.isArray(params)
        ? params.reduce((acc, obj) => ({ ...acc, ...obj }), {})
        : params;

    const searchParams = new URLSearchParams();

    Object.entries(normalized).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        // Apply custom key mapping
        const finalKey = keyMap[key] || key;

        if (Array.isArray(value)) {
            if (value.length) {
                searchParams.set(finalKey, value.join(","));
            }
        } else if (value !== "") {
            searchParams.set(finalKey, value);
        }
    });

    return searchParams.toString();
};

/**
 * Build Product Detail Page (PDP) URL
 * Pattern: /products/:slug/p/:sku
 *
 * @param {string} slug
 * @param {string|number} sku
 * @returns {string}
 */
export const urlPolicyPage = (slug) => {
    switch (slug) {
        case 'about-us':
            return `/pages/${slug}`;
        case 'contact-us':
            return `/${slug}`;
        case 'faq':
            return `/${slug}`;
        case 'privacy-policy':
        case 'refund-policy':
        case 'terms-conditions':
            return `/policy/${slug}`;

        default:
            return `/policy/${slug}`;
    }
};

/**
 * Build Product Detail Page (PDP) URL
 * Pattern: /products/:slug/p/:sku
 *
 * @param {string} slug
 * @param {string|number} sku
 * @returns {string}
 */
export const urlCompanyPage = (slug) => {
    switch (slug) {
        case 'about-us':
            return `/pages/${slug}`;
        case 'contact-us':
            return `/${slug}`;
        case 'faq':
            return `/${slug}`;
        case 'track/shipments':
            return `/${slug}`;

        default:
            return `/policy/${slug}`;
    }
};

/** 
 * 
 */

export const buildQuery = (filters) => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, values]) => {
        if (!values || values.length === 0) return;

        values.forEach((value) => {
            params.append(key, value);
        });
    });
    return params.toString();
};